/**
 * MemoryManager - Centralized tracking for intervals, timeouts, and event listeners
 * Based on BurrowTV-LITE-1local template (line ~10123)
 * 
 * Prevents memory leaks by tracking and cleaning up all timers and listeners
 * Singleton pattern for app-wide memory management
 */

class MemoryManagerClass {
  constructor() {
    if (MemoryManagerClass.instance) {
      return MemoryManagerClass.instance;
    }
    
    this.intervals = new Map(); // intervalId -> { id, callback, delay, context }
    this.timeouts = new Map();  // timeoutId -> { id, callback, delay, context }
    this.listeners = new Map(); // listenerId -> { element, event, handler, context }
    this.observers = new Map(); // observerId -> { observer, context }
    
    this.nextId = 1;
    
    MemoryManagerClass.instance = this;
  }
  
  /**
   * Create a tracked interval
   * @param {Function} callback - Function to execute
   * @param {number} delay - Delay in milliseconds
   * @param {string} context - Context/component name for debugging
   * @returns {number} Interval ID
   */
  setInterval(callback, delay, context = 'unknown') {
    const intervalId = window.setInterval(callback, delay);
    const trackingId = this.nextId++;
    
    this.intervals.set(intervalId, {
      id: trackingId,
      callback,
      delay,
      context,
      createdAt: new Date()
    });
    
    if (process.env.NODE_ENV === 'development') {
      console.log(`[MemoryManager] Interval ${trackingId} created in ${context}`);
    }
    
    return intervalId;
  }
  
  /**
   * Clear a tracked interval
   * @param {number} intervalId - Interval ID to clear
   */
  clearInterval(intervalId) {
    if (this.intervals.has(intervalId)) {
      const info = this.intervals.get(intervalId);
      window.clearInterval(intervalId);
      this.intervals.delete(intervalId);
      
      if (process.env.NODE_ENV === 'development') {
        console.log(`[MemoryManager] Interval ${info.id} cleared from ${info.context}`);
      }
    } else {
      // Still clear it even if not tracked
      window.clearInterval(intervalId);
    }
  }
  
  /**
   * Create a tracked timeout
   * @param {Function} callback - Function to execute
   * @param {number} delay - Delay in milliseconds
   * @param {string} context - Context/component name for debugging
   * @returns {number} Timeout ID
   */
  setTimeout(callback, delay, context = 'unknown') {
    const timeoutId = window.setTimeout(() => {
      callback();
      // Auto-cleanup after execution
      this.timeouts.delete(timeoutId);
    }, delay);
    
    const trackingId = this.nextId++;
    
    this.timeouts.set(timeoutId, {
      id: trackingId,
      callback,
      delay,
      context,
      createdAt: new Date()
    });
    
    if (process.env.NODE_ENV === 'development') {
      console.log(`[MemoryManager] Timeout ${trackingId} created in ${context}`);
    }
    
    return timeoutId;
  }
  
  /**
   * Clear a tracked timeout
   * @param {number} timeoutId - Timeout ID to clear
   */
  clearTimeout(timeoutId) {
    if (this.timeouts.has(timeoutId)) {
      const info = this.timeouts.get(timeoutId);
      window.clearTimeout(timeoutId);
      this.timeouts.delete(timeoutId);
      
      if (process.env.NODE_ENV === 'development') {
        console.log(`[MemoryManager] Timeout ${info.id} cleared from ${info.context}`);
      }
    } else {
      // Still clear it even if not tracked
      window.clearTimeout(timeoutId);
    }
  }
  
  /**
   * Add a tracked event listener
   * @param {Element} element - DOM element
   * @param {string} event - Event name
   * @param {Function} handler - Event handler
   * @param {object} options - Event listener options
   * @param {string} context - Context/component name for debugging
   * @returns {number} Listener ID
   */
  addEventListener(element, event, handler, options = {}, context = 'unknown') {
    if (!element || !element.addEventListener) {
      console.error('[MemoryManager] Invalid element for addEventListener');
      return null;
    }
    
    const listenerId = this.nextId++;
    
    element.addEventListener(event, handler, options);
    
    this.listeners.set(listenerId, {
      element,
      event,
      handler,
      options,
      context,
      createdAt: new Date()
    });
    
    if (process.env.NODE_ENV === 'development') {
      console.log(`[MemoryManager] Listener ${listenerId} added to ${element.tagName || 'element'}.${event} in ${context}`);
    }
    
    return listenerId;
  }
  
  /**
   * Remove a tracked event listener
   * @param {number} listenerId - Listener ID to remove
   */
  removeEventListener(listenerId) {
    if (this.listeners.has(listenerId)) {
      const { element, event, handler, options, context } = this.listeners.get(listenerId);
      
      if (element && element.removeEventListener) {
        element.removeEventListener(event, handler, options);
      }
      
      this.listeners.delete(listenerId);
      
      if (process.env.NODE_ENV === 'development') {
        console.log(`[MemoryManager] Listener ${listenerId} removed from ${context}`);
      }
    }
  }
  
  /**
   * Track an IntersectionObserver or ResizeObserver
   * @param {Observer} observer - Observer instance
   * @param {string} context - Context/component name for debugging
   * @returns {number} Observer ID
   */
  trackObserver(observer, context = 'unknown') {
    const observerId = this.nextId++;
    
    this.observers.set(observerId, {
      observer,
      context,
      createdAt: new Date()
    });
    
    if (process.env.NODE_ENV === 'development') {
      console.log(`[MemoryManager] Observer ${observerId} tracked in ${context}`);
    }
    
    return observerId;
  }
  
  /**
   * Disconnect and remove a tracked observer
   * @param {number} observerId - Observer ID
   */
  disconnectObserver(observerId) {
    if (this.observers.has(observerId)) {
      const { observer, context } = this.observers.get(observerId);
      
      if (observer && observer.disconnect) {
        observer.disconnect();
      }
      
      this.observers.delete(observerId);
      
      if (process.env.NODE_ENV === 'development') {
        console.log(`[MemoryManager] Observer ${observerId} disconnected from ${context}`);
      }
    }
  }
  
  /**
   * Clear all tracked resources for a specific context
   * Useful for component unmounting
   * @param {string} context - Context name to clear
   */
  clearContext(context) {
    let cleared = 0;
    
    // Clear intervals
    for (const [intervalId, info] of this.intervals.entries()) {
      if (info.context === context) {
        this.clearInterval(intervalId);
        cleared++;
      }
    }
    
    // Clear timeouts
    for (const [timeoutId, info] of this.timeouts.entries()) {
      if (info.context === context) {
        this.clearTimeout(timeoutId);
        cleared++;
      }
    }
    
    // Remove listeners
    for (const [listenerId, info] of this.listeners.entries()) {
      if (info.context === context) {
        this.removeEventListener(listenerId);
        cleared++;
      }
    }
    
    // Disconnect observers
    for (const [observerId, info] of this.observers.entries()) {
      if (info.context === context) {
        this.disconnectObserver(observerId);
        cleared++;
      }
    }
    
    if (process.env.NODE_ENV === 'development' && cleared > 0) {
      console.log(`[MemoryManager] Cleared ${cleared} resources from ${context}`);
    }
    
    return cleared;
  }
  
  /**
   * Clear all tracked resources (nuclear option)
   */
  clearAll() {
    // Clear all intervals
    for (const intervalId of this.intervals.keys()) {
      window.clearInterval(intervalId);
    }
    this.intervals.clear();
    
    // Clear all timeouts
    for (const timeoutId of this.timeouts.keys()) {
      window.clearTimeout(timeoutId);
    }
    this.timeouts.clear();
    
    // Remove all listeners
    for (const [listenerId, { element, event, handler, options }] of this.listeners.entries()) {
      if (element && element.removeEventListener) {
        element.removeEventListener(event, handler, options);
      }
    }
    this.listeners.clear();
    
    // Disconnect all observers
    for (const { observer } of this.observers.values()) {
      if (observer && observer.disconnect) {
        observer.disconnect();
      }
    }
    this.observers.clear();
    
    if (process.env.NODE_ENV === 'development') {
      console.log('[MemoryManager] All resources cleared');
    }
  }
  
  /**
   * Get memory usage statistics
   * @returns {object} Statistics object
   */
  getStats() {
    const stats = {
      intervals: this.intervals.size,
      timeouts: this.timeouts.size,
      listeners: this.listeners.size,
      observers: this.observers.size,
      total: this.intervals.size + this.timeouts.size + this.listeners.size + this.observers.size,
      byContext: {}
    };
    
    // Group by context
    for (const info of this.intervals.values()) {
      stats.byContext[info.context] = (stats.byContext[info.context] || 0) + 1;
    }
    for (const info of this.timeouts.values()) {
      stats.byContext[info.context] = (stats.byContext[info.context] || 0) + 1;
    }
    for (const info of this.listeners.values()) {
      stats.byContext[info.context] = (stats.byContext[info.context] || 0) + 1;
    }
    for (const info of this.observers.values()) {
      stats.byContext[info.context] = (stats.byContext[info.context] || 0) + 1;
    }
    
    return stats;
  }
  
  /**
   * Get detailed list of all tracked resources
   * For debugging memory leaks
   * @returns {object} All tracked resources
   */
  getDetails() {
    return {
      intervals: Array.from(this.intervals.entries()).map(([id, info]) => ({
        id,
        ...info,
        age: Date.now() - info.createdAt.getTime()
      })),
      timeouts: Array.from(this.timeouts.entries()).map(([id, info]) => ({
        id,
        ...info,
        age: Date.now() - info.createdAt.getTime()
      })),
      listeners: Array.from(this.listeners.entries()).map(([id, info]) => ({
        id,
        ...info,
        age: Date.now() - info.createdAt.getTime()
      })),
      observers: Array.from(this.observers.entries()).map(([id, info]) => ({
        id,
        ...info,
        age: Date.now() - info.createdAt.getTime()
      }))
    };
  }
}

// Create singleton instance
const MemoryManager = new MemoryManagerClass();

// Setup cleanup on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    MemoryManager.clearAll();
  });
}

export default MemoryManager;
